if (!customElements.get('gma-product-purchase')) {
  customElements.define(
    'gma-product-purchase',
    class GmaProductPurchase extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.anchor =
          document.getElementById(`gma-purchase-slot-${this.sectionId}`) || this.parentElement;
        this.mobileMediaQuery = window.matchMedia('(max-width: 749px)');
        this.currency = this.dataset.currency || 'AUD';
        this.locale = this.dataset.locale || document.documentElement.lang || 'en';
        this.unitPrice = Number(this.dataset.unitPrice || 0);
        this.compareAtPrice = Number(this.dataset.compareAtPrice || 0);
        this.titleVisible = true;

        this.updateLayout = this.updateLayout.bind(this);
        this.onVariantChange = this.onVariantChange.bind(this);
        this.onContextIntersection = this.onContextIntersection.bind(this);
        this.onStickScroll = this.onStickScroll.bind(this);

        this.atcButton = this.querySelector('.gma-purchase-atc-btn');
        this.atcLabel = this.atcButton?.querySelector('span');
        this.stickyContext = this.querySelector('.gma-purchase-sticky-context');
        this.contextSave = this.stickyContext?.querySelector('.gma-purchase-sticky-context__save');
        this.contextMedia = this.stickyContext?.querySelector('.gma-purchase-sticky-context__media');

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribeVariant = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange);
        }

        this.mobileMediaQuery.addEventListener('change', this.updateLayout);
        this.updateLayout();
        this.resetAtcLabel();
        this.updateSaveLabel();
      }

      disconnectedCallback() {
        this.teardownStickWatcher();
        this.teardownContextObserver();
        this.clearContextCollapseTimer();
        this.mobileMediaQuery?.removeEventListener('change', this.updateLayout);
        this.unsubscribeVariant?.();
        this.classList.remove('is-sticky', 'is-expanded', 'is-mobile-purchase');
        this.finishContextCollapse(true);
        this.removeStickSentinel();

        if (this.anchor && this.parentElement === document.body) {
          this.anchor.appendChild(this);
        }
      }

      onVariantChange(event) {
        if (event?.data?.sectionId && event.data.sectionId !== this.sectionId) return;

        const variant = event?.data?.variant;
        if (variant) {
          this.unitPrice = Number(variant.price || 0);
          this.compareAtPrice = Number(variant.compare_at_price || 0);
          this.dataset.unitPrice = String(this.unitPrice);
          this.dataset.compareAtPrice = String(this.compareAtPrice);
          this.updateContextMedia(variant);
        }

        this.resetAtcLabel();
        this.updateSaveLabel();
      }

      formatMoney(cents) {
        const amount = Number(cents || 0) / 100;
        try {
          const formatted = new Intl.NumberFormat(this.locale, {
            style: 'currency',
            currency: this.currency,
            currencyDisplay: 'narrowSymbol',
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
          }).format(amount);

          return formatted.replace(/([.,]00)(?!\d)/, '');
        } catch (error) {
          return `$${amount % 1 === 0 ? amount.toFixed(0) : amount.toFixed(2)}`;
        }
      }

      resetAtcLabel() {
        if (!this.atcButton || !this.atcLabel) return;

        const base =
          this.atcButton.dataset.gmaAtcBase || window.variantStrings?.addToCart || 'Add to cart';
        const text = (this.atcLabel.textContent || '').trim();
        const disabled = this.atcButton.hasAttribute('disabled');

        if (disabled) {
          const isAtcLike =
            text === base ||
            text === window.variantStrings?.addToCart ||
            text.startsWith(`${base} ·`);
          // Keep sold-out / limited-order labels; only normalize ATC-like copy.
          if (!isAtcLike) return;
        }

        this.atcLabel.textContent = base;
      }

      buildSaveLabel(price, compareAt) {
        const unit = Number(price || 0);
        const compare = Number(compareAt || 0);
        if (!(compare > unit)) return '';

        const savingCents = compare - unit;
        const savingDollars = savingCents / 100;
        const savingPercent = Math.round((savingCents * 100) / compare);

        if (savingDollars >= savingPercent) {
          if (savingDollars > 0) return `Save ${this.formatMoney(savingCents)}`;
          return '';
        }

        if (savingPercent > 0) return `Save ${savingPercent}%`;
        return '';
      }

      updateSaveLabel() {
        if (!this.contextSave) return;

        const label = this.buildSaveLabel(this.unitPrice, this.compareAtPrice);
        if (label) {
          this.contextSave.textContent = label;
          this.contextSave.classList.remove('is-empty');
          this.contextSave.removeAttribute('hidden');
        } else {
          this.contextSave.textContent = '';
          this.contextSave.classList.add('is-empty');
          this.contextSave.setAttribute('hidden', '');
        }
      }

      updateContextMedia(variant) {
        if (!this.contextMedia) return;

        const featured = variant?.featured_media || variant?.featured_image;
        if (!featured) return;

        const src =
          featured.preview_image?.src ||
          featured.src ||
          (typeof featured === 'string' ? featured : null);
        if (!src) return;

        let img = this.contextMedia.querySelector('.gma-purchase-sticky-context__img');
        if (!img) {
          img = document.createElement('img');
          img.className = 'gma-purchase-sticky-context__img';
          img.loading = 'lazy';
          img.alt = this.dataset.productTitle || '';
          this.contextMedia.appendChild(img);
        }

        const url = src.includes('?') ? `${src}&width=120` : `${src}?width=120`;
        img.src = url;
        if (featured.alt) img.alt = featured.alt;
      }

      ensureStickSentinel() {
        if (this.stickSentinel?.isConnected) return;

        const parent = this.parentElement;
        if (!parent) return;

        this.stickSentinel = document.createElement('div');
        this.stickSentinel.className = 'gma-purchase-stick-sentinel';
        this.stickSentinel.setAttribute('aria-hidden', 'true');
        this.stickSentinel.style.display = 'block';
        parent.insertBefore(this.stickSentinel, this);
      }

      removeStickSentinel() {
        this.stickSentinel?.remove();
        this.stickSentinel = null;
      }

      setupStickWatcher() {
        this.teardownStickWatcher();
        this.ensureStickSentinel();
        this.onStickScroll();
        window.addEventListener('scroll', this.onStickScroll, { passive: true });
        window.addEventListener('resize', this.onStickScroll, { passive: true });
      }

      teardownStickWatcher() {
        window.removeEventListener('scroll', this.onStickScroll);
        window.removeEventListener('resize', this.onStickScroll);
        if (this.stickRaf) {
          cancelAnimationFrame(this.stickRaf);
          this.stickRaf = null;
        }
      }

      onStickScroll() {
        if (this.stickRaf) return;
        this.stickRaf = requestAnimationFrame(() => {
          this.stickRaf = null;
          this.syncStuckState();
        });
      }

      /**
       * Compact in-flow height for pin math. Must stay stable after .is-sticky
       * (chrome padding / expand would otherwise raise the bar and immediately
       * fail the same threshold — stick/unstick thrash).
       */
      measureCompactPinHeight() {
        if (!this.classList.contains('is-sticky')) {
          this.compactPinHeight = Math.max(this.offsetHeight, 1);
        }

        if (this.compactPinHeight) return this.compactPinHeight;

        const row = this.querySelector('.gma-purchase-row');
        return Math.max(row?.offsetHeight || 1, 1);
      }

      syncStuckState() {
        if (!this.mobileMediaQuery.matches || !this.stickSentinel) {
          this.setStuck(false);
          return;
        }

        const pinLine = window.innerHeight - this.measureCompactPinHeight();
        const naturalTop = this.stickSentinel.getBoundingClientRect().top;

        // Stick once the natural top reaches the bottom pin line (bar flush with
        // viewport bottom). Stay stuck while the natural top remains above that line.
        this.setStuck(naturalTop <= pinLine + 1);
      }

      setStuck(stuck) {
        const wasStuck = this.classList.contains('is-sticky');
        if (stuck === wasStuck) {
          if (stuck) {
            this.refreshStickPlaceholder();
            this.syncExpandedFromTitle();
          }
          return;
        }

        if (stuck) {
          this.measureCompactPinHeight();
          this.classList.add('is-sticky');
          // Keep sentinel height at 0 — reserving the in-flow bar height leaves a
          // visible empty hole under pricing while the bar is fixed at the bottom.
          if (this.stickSentinel) this.stickSentinel.style.height = '';
          this.syncExpandedFromTitle();
        } else {
          this.setExpanded(false);
          this.classList.remove('is-sticky');
          this.compactPinHeight = null;
          if (this.stickSentinel) this.stickSentinel.style.height = '';
        }
      }

      refreshStickPlaceholder() {
        // Intentionally no layout placeholder while stuck (avoids empty gap).
        if (this.stickSentinel) this.stickSentinel.style.height = '';
      }

      setupContextObserver() {
        if (!this.stickyContext || typeof IntersectionObserver === 'undefined') return;

        this.teardownContextObserver();

        const productInfo =
          document.getElementById(`ProductInfo-${this.sectionId}`) ||
          document.querySelector('product-info') ||
          document;

        const titleEl =
          productInfo.querySelector?.('.product__title') ||
          productInfo.querySelector?.('.gma-product-heading') ||
          document.querySelector('.product__title') ||
          document.querySelector('.gma-product-heading');
        const pricingEl =
          productInfo.querySelector?.('.gma-everyday-pricing') ||
          document.querySelector('.gma-everyday-pricing') ||
          productInfo.querySelector?.('.price') ||
          document.querySelector('.price');

        this.observedTitle = titleEl || pricingEl;

        if (!this.observedTitle) {
          this.titleVisible = true;
          this.setExpanded(false);
          return;
        }

        this.titleVisible = true;

        this.contextObserver = new IntersectionObserver(this.onContextIntersection, {
          root: null,
          threshold: 0,
        });

        this.contextObserver.observe(this.observedTitle);
      }

      teardownContextObserver() {
        this.contextObserver?.disconnect();
        this.contextObserver = null;
        this.observedTitle = null;
        this.titleVisible = true;
        this.setExpanded(false);
      }

      onContextIntersection(entries) {
        for (const entry of entries) {
          if (entry.target === this.observedTitle) {
            this.titleVisible = entry.isIntersecting;
          }
        }

        this.syncExpandedFromTitle();
      }

      syncExpandedFromTitle() {
        // Context strip only while stuck and the title is off-screen.
        this.setExpanded(this.classList.contains('is-sticky') && !this.titleVisible);
      }

      setExpanded(expanded) {
        if (!this.stickyContext) return;

        const wantExpanded = Boolean(expanded && this.classList.contains('is-sticky'));
        const isExpanded = this.classList.contains('is-expanded');
        if (wantExpanded === isExpanded) {
          if (!wantExpanded) this.finishContextCollapse(true);
          return;
        }

        this.clearContextCollapseTimer();

        if (wantExpanded) {
          this.stickyContext.hidden = false;
          this.stickyContext.removeAttribute('hidden');
          this.stickyContext.setAttribute('aria-hidden', 'false');
          void this.stickyContext.offsetHeight;
          this.classList.add('is-expanded');
          requestAnimationFrame(() => this.refreshStickPlaceholder());
          return;
        }

        this.classList.remove('is-expanded');
        this.stickyContext.setAttribute('aria-hidden', 'true');

        if (this.prefersReducedMotion()) {
          this.finishContextCollapse(true);
          return;
        }

        const onTransitionEnd = (event) => {
          if (event.target !== this.stickyContext) return;
          if (event.propertyName !== 'grid-template-rows') return;
          this.stickyContext.removeEventListener('transitionend', onTransitionEnd);
          this.finishContextCollapse(false);
        };

        this.stickyContext.addEventListener('transitionend', onTransitionEnd);
        this.contextCollapseTimer = window.setTimeout(() => {
          this.stickyContext.removeEventListener('transitionend', onTransitionEnd);
          this.finishContextCollapse(false);
        }, 450);
      }

      prefersReducedMotion() {
        return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      }

      clearContextCollapseTimer() {
        if (this.contextCollapseTimer) {
          window.clearTimeout(this.contextCollapseTimer);
          this.contextCollapseTimer = null;
        }
      }

      finishContextCollapse(force) {
        this.clearContextCollapseTimer();
        if (!this.stickyContext) return;
        if (force || !this.classList.contains('is-expanded')) {
          this.stickyContext.setAttribute('hidden', '');
          this.stickyContext.hidden = true;
          this.stickyContext.setAttribute('aria-hidden', 'true');
        }
        requestAnimationFrame(() => this.refreshStickPlaceholder());
      }

      updateLayout() {
        if (this.parentElement === document.body && this.anchor) {
          this.anchor.appendChild(this);
        }

        this.anchor?.classList.remove('gma-purchase-slot--relocated');

        if (this.mobileMediaQuery.matches) {
          this.classList.add('is-mobile-purchase');
          this.setupStickWatcher();
          this.setupContextObserver();
          this.syncStuckState();
        } else {
          this.classList.remove('is-mobile-purchase');
          this.teardownStickWatcher();
          this.teardownContextObserver();
          this.removeStickSentinel();
          this.classList.remove('is-sticky', 'is-expanded');
          this.compactPinHeight = null;
          this.finishContextCollapse(true);
        }

        this.resetAtcLabel();
        window?.Shopify?.PaymentButton?.init();
      }
    }
  );
}
